import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Family, Parent, Student } from '@/types/models';
import FamilyInformation from '../sidebar/FamilyInformation';
import ParentsSection from '../sidebar/ParentsSection';
import StudentsSection from '../sidebar/StudentsSection';
import { Button } from '@/components/ui/button';
import FamilyDetailStatusBar from './FamilyDetailStatusBar';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface FamilyOverviewTabProps {
  family: Family;
  parents: Parent[];
  students: Student[];
  onAddParent: () => void;
  onAddStudent: () => void;
  onSendParentInvite: (parentId: string) => void;
  onEditParent: (parent: Parent) => void;
  onEditStudent?: (student: Student) => void;
  onDeleteParent?: (parent: Parent) => void;
  onDeleteFamily: () => void;
  onToggleActiveStatus?: (isActive: boolean) => void;
  onToggleParentActiveStatus?: (parent: Parent, isActive: boolean) => void;
  isDeleting: boolean;
}

const FamilyOverviewTab = ({
  family,
  parents,
  students,
  onAddParent,
  onAddStudent,
  onSendParentInvite,
  onEditParent,
  onEditStudent,
  onDeleteParent,
  onDeleteFamily,
  onToggleActiveStatus,
  onToggleParentActiveStatus,
  isDeleting
}: FamilyOverviewTabProps) => {
  return (
    <div className="space-y-4">
      <FamilyDetailStatusBar 
        family={family}
        parentsCount={parents.length}
        studentsCount={students.length}
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Parents</CardTitle>
            <Button 
              size="sm" 
              onClick={onAddParent}
              className="whitespace-nowrap"
            >
              Add Parent
            </Button>
          </CardHeader>
          <CardContent>
            <ParentsSection 
              parents={parents} 
              onAddParent={onAddParent}
              onSendInvite={onSendParentInvite}
              onEditParent={onEditParent}
              onDeleteParent={onDeleteParent}
              onToggleActiveStatus={onToggleParentActiveStatus}
              expanded
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Students</CardTitle>
            <Button 
              size="sm"
              onClick={onAddStudent}
              className="whitespace-nowrap"
            >
              Add Student
            </Button>
          </CardHeader>
          <CardContent>
            <StudentsSection
              students={students}
              onAddStudent={onAddStudent}
              onEditStudent={onEditStudent}
              expanded
            />
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Family Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="family-active-status" className="text-base">Family Status</Label>
                <p className="text-sm text-muted-foreground">
                  {family.is_active !== false 
                    ? "This family is currently active" 
                    : "This family is currently inactive"}
                </p>
              </div>
              <Switch
                id="family-active-status"
                checked={family.is_active !== false}
                onCheckedChange={onToggleActiveStatus}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-red-50 border-red-200">
        <CardHeader>
          <CardTitle className="text-red-600">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Deleting this family will permanently remove all associated records. This action cannot be undone.
            </p>
            <Button 
              variant="destructive" 
              onClick={onDeleteFamily}
              disabled={isDeleting}
            >
              Delete Family
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FamilyOverviewTab;
